import { useState } from "react";
import { Link } from "react-router-dom";
import { ProgramSummaryResponse } from "../../types/program.response";
import { useAuth } from "../../contexts/AuthContext";
import { useModal } from "../../contexts/ModalContext";
import { usersApi } from "../../services/api/users.api";
import { isRequirementFulfilled } from "../../utils/requirementResponseUtils";
import OverallProgress from "./OverallProgress";

interface ProgramInfoSidebarProps {
    program: ProgramSummaryResponse;
    onProgramUpdate: () => Promise<void>;
    /** 传入后"总体进度"的池行可点击(打开 pool 详情面板) */
    onPoolClick?: (poolKey: string) => void;
}

export default function ProgramInfoSidebar({ program, onProgramUpdate, onPoolClick }: ProgramInfoSidebarProps) {
    const { user, isLoggedIn, refreshAuth } = useAuth();
    const { openLoginModal } = useModal();
    const [updating, setUpdating] = useState(false);

    const isUserProgram = program.summary.isUserProgram;
    const hasConcentrations = program.concentrationNames.length > 0;

    const userProgram = user?.programs.find(p => p.programId === program.info.id);
    const selectedConcentration = userProgram?.concentrationNames[0] ?? null;

    const requiredCoursesCount = program.summary.requiredCoursesCount;
    const completedCoursesCount = program.summary.completedCoursesCount;

    const handleToggleProgram = async () => {
        if (!isLoggedIn) {
            openLoginModal();
            return;
        }
        if (!user) return;
        setUpdating(true);
        try {
            let updatedPrograms;
            if (isUserProgram) {
                updatedPrograms = user.programs.filter(p => p.programId !== program.info.id);
            } else {
                updatedPrograms = [
                    ...user.programs,
                    {
                        programId: program.info.id,
                        name: program.info.name,
                        type: program.info.type,
                        concentrationNames: [],
                    },
                ];
            }
            await usersApi.updateProfile({ programs: updatedPrograms });
            await refreshAuth();
            await onProgramUpdate();
        } catch (error) {
            console.error("Failed to update program:", error);
        } finally {
            setUpdating(false);
        }
    };

    const handleConcentrationSelect = async (name: string) => {
        if (!user || !userProgram || name === selectedConcentration) return;
        setUpdating(true);
        try {
            const updatedPrograms = user.programs.map(p =>
                p.programId === program.info.id
                    ? { ...p, concentrationNames: [name] }
                    : p
            );
            await usersApi.updateProfile({ programs: updatedPrograms });
            await refreshAuth();
            await onProgramUpdate();
        } catch (error) {
            console.error("Failed to update concentration:", error);
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div className="bg-gray-50 rounded-md p-3">
            <div className="border-b border-gray-200 pb-4">
                <h2 className="text-sm mb-2 font-bold">{program.info.name}</h2>
                {program.info.colleges.map((college) => (
                    <div key={college.id} className="text-xs text-gray-500">{college.name}</div>
                ))}

                {program.info.type !== 'college' && (
                    <button
                        onClick={handleToggleProgram}
                        disabled={updating}
                        className={`mt-4 w-full rounded-md px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            isUserProgram
                                ? "border border-gray-300 text-gray-700 hover:bg-gray-100"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                        {updating
                            ? "Updating..."
                            : isUserProgram
                                ? "Remove from My Programs"
                                : "Add to My Programs"}
                    </button>
                )}
            </div>

            {hasConcentrations && isUserProgram && (
                <div className="border-b border-gray-200 py-3">
                    <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Concentrations</h2>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {program.concentrationNames.map((name) => {
                            const isSelected = name === selectedConcentration;
                            return (
                                <button
                                    key={name}
                                    onClick={() => handleConcentrationSelect(name)}
                                    disabled={updating}
                                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                        isSelected
                                            ? "bg-blue-100 text-blue-700 ring-1 ring-blue-300"
                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                    }`}
                                >
                                    {name}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <OverallProgress
                completedCoursesCount={completedCoursesCount}
                requiredCoursesCount={requiredCoursesCount}
                poolResults={program.poolResults}
                onPoolClick={onPoolClick}
            />

            <div>
                <h2 className="text-sm mt-2 font-semibold text-gray-500 uppercase">Requirements</h2>
                <div className="flex flex-col gap-0.5 py-2">{program.requirements.map((requirement) => {
                    const fulfilled = isRequirementFulfilled(requirement);
                    return (
                        <Link
                            key={requirement.info.id}
                            to={`/programs/${program.info.id}/requirements/${requirement.info.id}`}
                            className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded-md transition-colors ${
                                fulfilled ? "text-gray-400" : "text-gray-800 hover:bg-gray-100"
                            }`}
                        >
                            {fulfilled ? (
                                <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <div className="w-3.5 h-3.5 rounded-full border-[1.5px] border-gray-300 flex-shrink-0" />
                            )}
                            {requirement.info.name}
                        </Link>
                    );
                })}
                </div>
            </div>
            <p className="mt-4 pt-3 border-t border-gray-200 text-xs text-gray-500 leading-snug">
                For planning only — this does not guarantee that you've met all requirements. Always verify with official sources.
            </p>
            <p className="mt-1 pt-2 text-xs text-gray-500 leading-snug">
                Found an issue?{' '}
                <a href="https://forms.gle/QjjAxdeXQms7rNCF8" target="_blank" rel="noopener noreferrer" className="underline text-blue-400 hover:text-blue-600">Report here</a>
            </p>
        </div>
    );
}
